import { createSlice } from "@reduxjs/toolkit";

import { createAppAsyncThunk } from "Store/createAppAsyncThunk";

import { getEnvironmentActivitiesRequest } from "Api/activity/getEnvironmentActivities.request";

import { DEPLOYMENT_ACTIVITY_TYPES } from "./deploymentActivityTypes";

import type { ActivityTypeFilters } from "Api/activity/types";
import type { PayloadAction } from "@reduxjs/toolkit";

export interface LatestDeploymentActivityParams {
  projectId: string;
  environmentId: string;
}

export interface LatestDeploymentActivityEntry {
  /**
   * The newest COMPLETED deployment activity on the environment.
   *
   * `null` is a real answer, not "unknown": the environment has no deployment
   * activity left in retention. `hasLoaded` is what separates the two.
   */
  activityId: string | null;
  isLoading: boolean;
  hasLoaded: boolean;
}

export interface LatestDeploymentActivityState {
  byEnvironment: Record<string, LatestDeploymentActivityEntry>;
}

/** Same `projectId/environmentId` shape `environmentTasks.byEnvironment` uses. */
export const latestDeploymentActivityKey = ({
  projectId,
  environmentId
}: LatestDeploymentActivityParams) => `${projectId}/${environmentId}`;

const initialState: LatestDeploymentActivityState = { byEnvironment: {} };

/**
 * Fetches the environment's newest completed deployment activity.
 *
 * `count: 1` against a type-filtered, `state=complete` collection, which the API
 * returns newest-first. That single id answers the whole recency question — no
 * timestamp join, and none of the tolerance the plan rejected for matching
 * `deployment_state.last_deployment_at` (it lands within ~150ms of the
 * activity's own `completed_at`, so it can order but cannot identify).
 *
 * `state=complete` is load-bearing. A redeploy that is still running is a more
 * recent deployment activity, and without this filter starting one would hide
 * the CTA on the very failure being redeployed.
 */
export const loadLatestDeploymentActivity = createAppAsyncThunk<
  { key: string; activityId: string | null },
  LatestDeploymentActivityParams
>(
  "latestDeploymentActivity/load",
  async ({ projectId, environmentId }) => {
    const activities = await getEnvironmentActivitiesRequest({
      projectId,
      encodedEnvId: encodeURIComponent(environmentId),
      // `ActivityTypeFilters` is declared as a tuple of every known filter
      // string, but it is a flat `string[]` everywhere it is used — see
      // `activity.slice.ts:48`, which calls `.includes()` on it. Cast rather
      // than spread that fiction into a new list.
      filters: DEPLOYMENT_ACTIVITY_TYPES as unknown as ActivityTypeFilters,
      state: "complete",
      count: 1
    });

    return {
      key: latestDeploymentActivityKey({ projectId, environmentId }),
      activityId: activities[0]?.id ?? null
    };
  },
  {
    /**
     * Makes the thunk itself idempotent, so every surface asking about one
     * environment costs one request. The activity list and the log modal can
     * both mount for the same environment in the same tick.
     */
    condition: ({ projectId, environmentId }, { getState }) => {
      if (!projectId || !environmentId) return false;

      const entry =
        getState().latestDeploymentActivity.byEnvironment[
          latestDeploymentActivityKey({ projectId, environmentId })
        ];

      return !entry?.isLoading && !entry?.hasLoaded;
    }
  }
);

/**
 * Which activity is each environment's current deployment.
 *
 * Server state, but not the kind the project websocket refreshes, so it is not
 * folded into `environmentTasks` or the activity slice. It answers one question
 * — "is this failure still the environment's live state?" — and it is fetched
 * lazily, only for environments a user actually opens a failed activity on.
 *
 * Cached for the session. A deployment landing while the user reads an old log
 * leaves the answer stale, and the cost of that is a link that opens a panel
 * over a superseded failure. Recoverable, and cheaper than invalidating on every
 * websocket activity event.
 */
const latestDeploymentActivity = createSlice({
  name: "latestDeploymentActivity",
  initialState,
  reducers: {
    /** Drops a cached answer so the next ask refetches. For the websocket, later. */
    latestDeploymentActivityInvalidated(
      state,
      { payload }: PayloadAction<LatestDeploymentActivityParams>
    ) {
      delete state.byEnvironment[latestDeploymentActivityKey(payload)];
    }
  },
  extraReducers: builder => {
    builder
      .addCase(loadLatestDeploymentActivity.pending, (state, { meta }) => {
        const key = latestDeploymentActivityKey(meta.arg);
        state.byEnvironment[key] = {
          activityId: state.byEnvironment[key]?.activityId ?? null,
          isLoading: true,
          hasLoaded: state.byEnvironment[key]?.hasLoaded ?? false
        };
      })
      .addCase(
        loadLatestDeploymentActivity.fulfilled,
        (state, { payload: { key, activityId } }) => {
          state.byEnvironment[key] = {
            activityId,
            isLoading: false,
            hasLoaded: true
          };
        }
      )
      .addCase(loadLatestDeploymentActivity.rejected, (state, { meta }) => {
        // Left `hasLoaded: false` on purpose. A failed lookup must not read as
        // "this environment has no deployments", which would hide the CTA for
        // the rest of the session on one dropped request.
        state.byEnvironment[latestDeploymentActivityKey(meta.arg)] = {
          activityId: null,
          isLoading: false,
          hasLoaded: false
        };
      });
  }
});

export const { latestDeploymentActivityInvalidated } =
  latestDeploymentActivity.actions;

export default latestDeploymentActivity.reducer;
